import moment from "moment/min/moment-with-locales";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Row, Stack, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import DataDescription from "../components/DataDescription";
import { getRadioName, mapItemType } from "../components/HelperFunctions";
import {
    useFetchData,
    useLatestCalculatedDate,
    useSetDaypartOnChange,
    useSetDaysOnPerspectiveChange,
    useSetPerspectiveOnDaysChange,
    useResolvedRadios,
} from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import DatesPicker, { latest2Weeks, latestDay, latestMonth, latestQuarter, latestWeek } from "../components/Pickers/DatesPicker";
import DaypartPicker from "../components/Pickers/DaypartPicker";
import DaysPicker from "../components/Pickers/DaysPicker";
import HoursPicker from "../components/Pickers/HoursPicker";
import MusicSelectionTypePicker from "../components/Pickers/MusicSelectionTypePicker";
import MinMaxSpinsPicker from "../components/Pickers/MinMaxSpinsPicker";
import PerspectivePicker from "../components/Pickers/PerspectivePicker";
import StationPicker from "../components/Pickers/StationPicker";
import { useUserFormState } from "../components/UserSelection";
import ContentBalanceTrendChart from "../components/dashboard/ContentBalanceTrendChart";
import LoadingIndicator from "../components/LoadingIndicator";
import { DashboardPanel, DataContent, DescriptionWithDownloadRow, InputSelection, ModulePage } from "../components/pageSections";
import MinMaxYearPicker from "../components/Pickers/MinMaxYearPicker";
import ContentBalanceAverageCard from "../components/contentBalance/ContentBalanceAverageCard";
import SongsCard from "../components/health/SongsCard";
const extraDefaults = { station2: [], selectionType: "all", minSpins: 0, maxSpins: 0, minYear: 0, maxYear: new Date().getFullYear() };

export default function ContentBalance() {
    const moduleName = useMemo(() => "content_balance", []);
    const moduleId = useMemo(() => ({ 0: 38, 1: 38, 2: 38 }), []);

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName, extraDefaults);
    const [contentBalanceLoading, contentBalanceErrorCode, contentBalanceData, fetchContentBalanceData] = useFetchData();
    const [contentSongLoading, contentSongErrorCode, contentSongData, fetchContentSongData] = useFetchData();
    const [contentBalanceAvgLoadingOne, contentBalanceAvgErrorCodeOne, contentBalanceAvgDataOne, fetchContentBalanceAvgDataOne] = useFetchData();
    const [contentBalanceLoadingOne, contentBalanceErrorCodeOne, contentBalanceDataOne, fetchContentBalanceDataOne] = useFetchData();
    const [contentBalanceAvgLoadingTwo, contentBalanceAvgErrorCodeTwo, contentBalanceAvgDataTwo, fetchContentBalanceAvgDataTwo, setContentBalanceAvgDataTwo] =
        useFetchData();
    const [contentBalanceLoadingTwo, contentBalanceErrorCodeTwo, contentBalanceDataTwo, fetchContentBalanceDataTwo, setContentBalanceDataTwo] = useFetchData();
    const [subLoading, setSubLoading] = useState(false);
    const [latestCalculatedDate, setLatestCalculatedDate] = useState(null);
    const resolvedRadios = useResolvedRadios(true);
    const advancedInputRef = useRef(null);
    const contentBalanceRef = useRef(null);
    const [fileName, setFileName] = useState("Content Balance");
    const [grouping, setGrouping] = useState(0);
    const [allowedGroupings, setAllowedGroupings] = useState([]);
    const [defaultGrouping, setDefaultGrouping] = useState(0);
    const [lastYearAverages] = useState(false);
    const currentYear = new Date().getFullYear();

    const predefinedRanges = useMemo(() => {
        if (latestCalculatedDate) {
            if (formData.dateRange === undefined) {
                setFormData(current => ({ ...current, dateRange: latestWeek(latestCalculatedDate, true) }));
            }
            return [
                latestDay(latestCalculatedDate),
                latestWeek(latestCalculatedDate),
                latest2Weeks(latestCalculatedDate),
                latestMonth(latestCalculatedDate),
                latestQuarter(latestCalculatedDate),
            ];
        }
    }, [formData.dateRange, latestCalculatedDate, setFormData]);

    const demo = useMemo(() => [{ data_type: 0, listener_group: 0 }], []);
    useLatestCalculatedDate(setLatestCalculatedDate, moduleId, formData.station, demo);
    useSetPerspectiveOnDaysChange(setFormData, formData.days);
    useSetDaysOnPerspectiveChange(setFormData, formData.perspective);
    useSetDaypartOnChange(setFormData, formData.station, formData.days, formData.hourRange);

    useEffect(() => {
        if (formData.dateRange?.length === 2 && formData.dateRange[0] && formData.dateRange[1]) {
            const start = formData.dateRange[0];
            const end = formData.dateRange[1];
            const differenceMs = end.getTime() - start.getTime();
            const differenceDays = Math.ceil(differenceMs / (1000 * 60 * 60 * 24));

            if (differenceDays <= 1) {
                setAllowedGroupings([0, 1, 2]);
                setDefaultGrouping(1);
            } else if (differenceDays <= 7) {
                setAllowedGroupings([0, 1, 2, 3]);
                setDefaultGrouping(2);
            } else if (differenceDays <= 14) {
                setAllowedGroupings([1, 2, 3, 4]);
                setDefaultGrouping(2);
            } else if (differenceDays <= 31) {
                setAllowedGroupings([1, 2, 3, 4]);
                setDefaultGrouping(3);
            } else if (differenceDays <= 92) {
                setAllowedGroupings([1, 2, 3, 4, 5]);
                setDefaultGrouping(4);
            } else if (differenceDays <= 184) {
                setAllowedGroupings([3, 4, 5]);
                setDefaultGrouping(5);
            } else {
                setAllowedGroupings([3, 4, 5]);
                setDefaultGrouping(5);
            }
        } else {
            setAllowedGroupings([]);
        }
    }, [formData.dateRange]);

    async function getData(selectedLastYearAverage, selectedGrouping, onlySubLoading = false) {
        if (onlySubLoading) setSubLoading(true);

        const radioOne = formData.station[0]?.radio_id ?? null;
        const radioTwo = formData.station2?.[0]?.radio_id ?? null;
        const input = {
            radio_ids: [radioOne, radioTwo].filter(id => id !== null),
            start_datetime: moment(formData.dateRange[0]).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
            end_datetime: moment(formData.dateRange[1]).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
            start_time: moment().hours(formData.hourRange[0]).minutes(0).seconds(0).format("HH:mm:ss"),
            end_time: moment()
                .hours(formData.hourRange[1] - 1)
                .minutes(59)
                .seconds(59)
                .format("HH:mm:ss"),
            days: formData.days,
            limit: formData.selectionType === "top" ? 50 : 0,
            new: formData.selectionType === "new",
            min_spins: formData.minSpins,
            max_spins: formData.maxSpins,
            min_release_year: formData.minYear,
            max_release_year: formData.maxYear,
            grouping: selectedGrouping,
            include_averages: true,
            last_year_as_averages: selectedLastYearAverage,
        };

        const createRadioInput = radioId => ({
            radio_id: radioId,
            start_datetime: input.start_datetime,
            end_datetime: input.end_datetime,
            start_time: input.start_time,
            end_time: input.end_time,
            days: input.days,
            average: false,
        });

        const createAverageInput = radioId => ({
            radio_id: radioId,
            start_datetime: input.start_datetime,
            end_datetime: input.end_datetime,
            start_time: input.start_time,
            end_time: input.end_time,
            days: input.days,
            average: true,
        });

        const requests = [
            fetchContentBalanceData("comparison/content_balance/", input),
            // fetchContentSongData("health/content_balance/", input),

            fetchContentBalanceDataOne("content_balance/", createRadioInput(radioOne)),
            fetchContentBalanceAvgDataOne("content_balance/", createAverageInput(radioOne)),
        ];

        if (radioTwo !== null) {
            requests.push(
                fetchContentBalanceDataTwo("content_balance/", createRadioInput(radioTwo)),
                fetchContentBalanceAvgDataTwo("content_balance/", createAverageInput(radioTwo)),
            );
        } else {
            setContentBalanceDataTwo(null);
            setContentBalanceAvgDataTwo(null);
        }

        await Promise.all(requests);

        if (onlySubLoading) setSubLoading(false);
    }

    async function handleFormSubmit(event) {
        event.preventDefault();
        advancedInputRef.current.closeAdvanced();
        await getData(lastYearAverages, defaultGrouping);
        setGrouping(defaultGrouping);
        await saveFormData();
        await saveFormData(formData.station[0].radio_id);
    }

    const csvHeaders = useMemo(() => {
        if (!resolvedRadios || !contentBalanceData) return;

        const firstRadio = getRadioName(contentBalanceData.balances[0].radios[0].radio_id, resolvedRadios);
        const secondRadio = contentBalanceData.balances[0].radios[1] ? getRadioName(contentBalanceData.balances[0].radios[1].radio_id, resolvedRadios) : null;

        setFileName(
            `Content Balance for ${firstRadio}${secondRadio ? " and " + secondRadio : ""} from ${moment(contentBalanceData.start_datetime).format(
                "DD-MM-YYYY",
            )} to ${moment(contentBalanceData.end_datetime).format("DD-MM-YYYY")}`,
        );

        const includesTime = [1, 2].includes(Number(contentBalanceData.grouping));
        const headers = [
            includesTime ? "Time / Date" : "Date",
            ...contentBalanceData.balances[0].radios[0].balances.map(x => `${firstRadio} ${mapItemType(x.item_type)}`),
            ...(secondRadio ? contentBalanceData.balances[0].radios[1].balances.map(x => `${secondRadio} ${mapItemType(x.item_type)}`) : []),
        ];

        return headers;
    }, [contentBalanceData, formData.dateRange, resolvedRadios]);

    const csvData = useMemo(() => {
        if (!contentBalanceData) return;

        const includesTime = [1, 2].includes(Number(contentBalanceData.grouping));

        return contentBalanceData.balances.map(({ date, radios }) => {
            const [firstRadio, secondRadio] = radios;

            const firstRadioBalances = firstRadio.balances.map(x => `${x.value.toFixed(2)}%`);
            const secondRadioBalances = secondRadio ? secondRadio.balances.map(x => `${x.value.toFixed(2)}%`) : [];

            return [moment(date).format(includesTime ? "HH:mm DD-MM-YYYY" : "DD-MM-YYYY"), ...firstRadioBalances, ...secondRadioBalances];
        });
    }, [contentBalanceData]);

    async function handleGroupingChange(value) {
        setGrouping(value);
        await getData(lastYearAverages, value, true);
    }

    return (
        <ModulePage>
            <ModulePage.Title>Content Balance</ModulePage.Title>
            <ModulePage.InputSelection handleFormSubmit={handleFormSubmit} disabled={!formData.station.length || !formData.dateRange || !formData.days.length}>
                <InputSelection.Main>
                    <StationPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        includeCompetitors={true}
                        label="Station 1"
                        loadOnChange={loadFormData}
                        colXl={2}
                    />
                    <StationPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        disabled={!formData.station.length}
                        includeCompetitors={true}
                        formKey="station2"
                        label="Station 2"
                        colXl={2}
                    />
                    <DatesPicker formData={formData} setFormData={setFormData} predefinedRanges={predefinedRanges} latestAvailableDate={latestCalculatedDate} />
                    <DaypartPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <MusicSelectionTypePicker formData={formData} setFormData={setFormData} name={moduleName} />
                </InputSelection.Main>
                <InputSelection.Advanced moduleName={moduleName} ref={advancedInputRef}>
                    <DaysPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <HoursPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <PerspectivePicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <MinMaxYearPicker filterData={formData} setFilterData={setFormData} name={moduleName} />
                    <MinMaxSpinsPicker filterData={formData} setFilterData={setFormData} name={moduleName} />
                </InputSelection.Advanced>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={contentBalanceLoading && !subLoading} errorCode={contentBalanceErrorCode} multiPanel>
                <DataContent.Placeholder show={!contentBalanceData}>
                    <PageDesciption
                        title={"Content Balance"}
                        className="bg-white p-4 px-4"
                        whatList={[
                            "Compare how music, talk, commercials and other content types develop over time",
                            "View one or two stations in the same trend",
                            "Change grouping on the fly from hours to days, weeks or months",
                        ]}
                        knowList={[
                            { text: "Switch between latest 13 weeks and last year averages for quick context" },
                            { text: "Use dayparts and weekday filters to isolate programming changes" },
                        ]}
                        relatedLinks={[
                            {
                                title: "Tactical Observation",
                                url: "https://radioanalyzer.com/blog/tactical-observation",
                            },
                        ]}
                        videoSrc="https://www.youtube.com/embed/LTvc4ahAt58?si=Kt1-jZ7HBX_aPPx-"
                    />
                </DataContent.Placeholder>
                <Container fluid>
                    {contentBalanceData && (
                        <>
                            <DashboardPanel containerRef={contentBalanceRef}>
                                <Col xs={12} xl={12}>
                                    <DashboardPanel.Title>Content Balance</DashboardPanel.Title>
                                    <DescriptionWithDownloadRow
                                        csvHeaders={csvHeaders}
                                        data={csvData}
                                        filename={fileName}
                                        screenshotContainer={contentBalanceRef}>
                                        <DashboardPanel.Description fileName="Content Balance Trend" dataReady={!!contentBalanceData}>
                                            <DataDescription>
                                                {contentBalanceData.input.radio_ids.length < 2 ? (
                                                    <DataDescription.Station radioId={contentBalanceData.input.radio_ids[0]} />
                                                ) : (
                                                    <>
                                                        <DataDescription.Station label="Station 1" radioId={contentBalanceData.input.radio_ids[0] ?? ""} />
                                                        <DataDescription.Station label="Station 2" radioId={contentBalanceData.input.radio_ids[1] ?? ""} />
                                                    </>
                                                )}
                                                <DataDescription.DateRange
                                                    startDate={contentBalanceData?.input.start_datetime}
                                                    endDate={contentBalanceData.input?.end_datetime}
                                                />
                                                <DataDescription.TimeRange
                                                    startTime={contentBalanceData.input?.start_time}
                                                    endTime={contentBalanceData.input?.end_time}
                                                />
                                                <DataDescription.Perspective days={contentBalanceData.input?.days} />

                                                {(contentBalanceData.input.min_release_year > 0 ||
                                                    (contentBalanceData.input.max_release_year > 0 &&
                                                        contentBalanceData.input.max_release_year !== new Date().getFullYear())) && (
                                                    <DataDescription.Simple
                                                        label="Release Year"
                                                        value={`${contentBalanceData.input.min_release_year} - ${contentBalanceData.input.max_release_year}`}
                                                    />
                                                )}
                                                {(contentBalanceData.input?.min_spins > 0 || contentBalanceData.input?.max_spins > 0) && (
                                                    <DataDescription.Simple
                                                        label="Spins"
                                                        value={`${contentBalanceData.input?.min_spins} - ${contentBalanceData.input?.max_spins || "∞"}`}
                                                    />
                                                )}
                                            </DataDescription>
                                        </DashboardPanel.Description>
                                    </DescriptionWithDownloadRow>
                                    <DashboardPanel.DataContent>
                                        <Stack direction="horizontal" gap={1} className="flex-wrap column-gap-4 row-gap-2 mb-2">
                                            <Stack className="d-flex flex-md-row flex-column" gap={2}>
                                                <span className="justify-self-center align-self-md-center">Grouping</span>
                                                <ToggleButtonGroup
                                                    type="radio"
                                                    name="content-balance-grouping"
                                                    value={grouping}
                                                    onChange={handleGroupingChange}
                                                    className="ra-switcher flex-wrap flex-md-nowrap">
                                                    <ToggleButton
                                                        id={`${moduleName}-content-balance-grouping-hours`}
                                                        variant="ra-dark-blue-outline"
                                                        value={1}
                                                        disabled={!allowedGroupings.includes(1)}>
                                                        Hours
                                                    </ToggleButton>
                                                    <ToggleButton
                                                        id={`${moduleName}-content-balance-grouping-3-hours`}
                                                        variant="ra-dark-blue-outline"
                                                        value={2}
                                                        disabled={!allowedGroupings.includes(2)}>
                                                        3 Hours
                                                    </ToggleButton>
                                                    <ToggleButton
                                                        id={`${moduleName}-content-balance-grouping-days`}
                                                        variant="ra-dark-blue-outline"
                                                        value={3}
                                                        disabled={!allowedGroupings.includes(3)}>
                                                        Days
                                                    </ToggleButton>
                                                    <ToggleButton
                                                        id={`${moduleName}-content-balance-grouping-weeks`}
                                                        variant="ra-dark-blue-outline"
                                                        value={4}
                                                        disabled={!allowedGroupings.includes(4)}>
                                                        Weeks
                                                    </ToggleButton>
                                                    <ToggleButton
                                                        id={`${moduleName}-content-balance-grouping-months`}
                                                        variant="ra-dark-blue-outline"
                                                        value={5}
                                                        disabled={!allowedGroupings.includes(5)}>
                                                        Months
                                                    </ToggleButton>
                                                </ToggleButtonGroup>
                                            </Stack>
                                        </Stack>
                                        {subLoading ? (
                                            <div className="d-flex flex-grow-1">
                                                <LoadingIndicator width={75} />
                                            </div>
                                        ) : (
                                            <ContentBalanceTrendChart
                                                data={contentBalanceData.balances}
                                                averages={contentBalanceData.averages}
                                                loading={contentBalanceLoading}
                                                error={contentBalanceErrorCode}
                                                grouping={contentBalanceData.grouping}
                                                averageLabel={lastYearAverages ? "Last Year" : "13 Weeks"}
                                            />
                                        )}

                                        {(contentBalanceAvgDataOne || contentBalanceAvgDataTwo) && (
                                            <>
                                                <hr className="m-2" />
                                                <Row className="g-3 d-flex justify-content-center pb-2">
                                                    {contentBalanceAvgDataOne && contentBalanceDataOne && (
                                                        <div style={{ width: 400, maxWidth: "100%" }}>
                                                            <ContentBalanceAverageCard
                                                                data={contentBalanceDataOne}
                                                                avgData={contentBalanceAvgDataOne}
                                                                dataLoading={contentBalanceLoadingOne}
                                                                avgDataLoading={contentBalanceAvgLoadingOne}
                                                                error={contentBalanceErrorCodeOne ?? contentBalanceAvgErrorCodeOne}
                                                                radioName={getRadioName(contentBalanceData.input.radio_ids[0], resolvedRadios)}
                                                            />
                                                        </div>
                                                    )}
                                                    {contentBalanceAvgDataTwo && contentBalanceDataTwo && (
                                                        <div style={{ width: 400, maxWidth: "100%" }}>
                                                            <ContentBalanceAverageCard
                                                                data={contentBalanceDataTwo}
                                                                avgData={contentBalanceAvgDataTwo}
                                                                dataLoading={contentBalanceLoadingTwo}
                                                                avgDataLoading={contentBalanceAvgLoadingTwo}
                                                                error={contentBalanceErrorCodeTwo ?? contentBalanceAvgErrorCodeTwo}
                                                                radioName={getRadioName(contentBalanceData.input.radio_ids[1], resolvedRadios)}
                                                            />
                                                        </div>
                                                    )}
                                                </Row>
                                                <hr className="m-2" />
                                            </>
                                        )}
                                    </DashboardPanel.DataContent>
                                </Col>
                            </DashboardPanel>
                        </>
                    )}
                </Container>
            </ModulePage.DataContent>
        </ModulePage>
    );
}
